import React, { useEffect } from 'react';

const Quote = ({ children }) => (
  <div style={{ background: 'rgba(255,255,255,0.15)', borderLeft: '4px solid rgba(255,255,255,0.4)', padding: '1.25rem 1.5rem', borderRadius: '8px', margin: '1rem 0', fontStyle: 'italic', lineHeight: 1.7 }}>
    {children}
  </div>
);

const H2 = ({ children }) => <h2 style={{ color: 'white', marginTop: '2.5rem', marginBottom: '1rem' }}>{children}</h2>;
const H3 = ({ children }) => <h3 style={{ color: 'rgba(255,255,255,0.9)', marginTop: '1.5rem', marginBottom: '0.75rem', fontSize: '1.2rem' }}>{children}</h3>;

const RelatedLinks = ({ current }) => {
  const all = Object.entries(PAGES).filter(([k]) => k !== current);
  const show = all.slice(0, 5);
  return (
    <div style={{ marginTop: '3rem', paddingTop: '2rem', borderTop: '1px solid rgba(255,255,255,0.2)' }}>
      <H2>Related Guides</H2>
      <ul style={{ listStyle: 'none', padding: 0 }}>
        {show.map(([path, page]) => (
          <li key={path} style={{ marginBottom: '0.5rem' }}>
            <a href={path} style={{ color: '#90cdf4', textDecoration: 'underline' }}>{page.title}</a>
          </li>
        ))}
      </ul>
    </div>
  );
};

const CTA = ({ onGetStarted }) => (
  <div style={{ textAlign: 'center', margin: '2.5rem 0 1rem' }}>
    <p style={{ fontSize: '1.1rem', marginBottom: '1rem' }}>Need help writing an obituary? Our AI-powered tool creates a beautiful, personalized obituary in minutes.</p>
    <button className="btn btn-lg" onClick={onGetStarted} style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white', border: 'none', padding: '0.75rem 2rem', borderRadius: '50px', fontWeight: 600 }}>
      Create an Obituary Now
    </button>
  </div>
);

// ─── PAGE CONTENT ───────────────────────────────────────────────

const PAGES = {

  '/obituary-examples-for-mother': {
    title: 'Obituary Examples for a Mother — Templates & Inspiration',
    description: 'Heartfelt obituary examples for a mother. Includes warm, formal, and faith-based templates you can customize for your own use.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Obituary Examples for a Mother</h1>
      <p>Writing an obituary for your mother is one of the hardest things you'll ever do. The woman who shaped your life deserves words that truly honor her — but finding those words during grief can feel impossible. These examples provide a starting point you can personalize.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Example 1: Warm & Personal</H2>
      <Quote>
        <p><strong>Margaret Ann Thompson</strong>, 78, of Cedar Rapids, Iowa, passed away peacefully on March 15, 2026, surrounded by her family.</p>
        <p>Margaret — "Maggie" to everyone who loved her — was born on June 3, 1947, to Robert and Helen Walsh. She graduated from Kennedy High School in 1965 and went on to earn her nursing degree from the University of Iowa.</p>
        <p>Maggie spent 35 years as a pediatric nurse, touching the lives of countless families. But her greatest joy was her own family. She married her high school sweetheart, David Thompson, in 1968. Together they built a life full of Sunday dinners, road trips in the family station wagon, and a home where the door was always open.</p>
        <p>She is survived by her husband David; her children Sarah (Michael) Anderson, James (Lisa) Thompson, and Katie Thompson-Rivera; her seven grandchildren; and her sister, Dorothy Walsh-Bennett. She was preceded in death by her parents and her brother, Thomas Walsh.</p>
        <p>A celebration of life will be held Saturday, March 22, at 2:00 PM at First United Methodist Church, Cedar Rapids. In lieu of flowers, the family suggests donations to the Pediatric Nursing Foundation.</p>
      </Quote>

      <H3>What makes this work</H3>
      <ul>
        <li>Uses her nickname to create warmth and familiarity</li>
        <li>Highlights specific details (station wagon, Sunday dinners) that paint a picture</li>
        <li>Balances professional accomplishments with family life</li>
        <li>Includes service details and charitable alternatives to flowers</li>
      </ul>

      <H2>Example 2: Formal & Detailed</H2>
      <Quote>
        <p><strong>Dr. Catherine Elizabeth Monroe</strong>, 82, a distinguished educator and devoted mother, died on February 28, 2026, at her home in Savannah, Georgia.</p>
        <p>Born on November 12, 1943, in Boston, Massachusetts, to Judge William Monroe and Eleanor Hastings Monroe, Catherine graduated summa cum laude from Wellesley College in 1965 and earned her Ph.D. in English Literature from Yale University in 1971.</p>
        <p>Dr. Monroe served as a professor of English at the University of Georgia for over three decades, where she was beloved by students and recognized with multiple teaching awards, including the Meigs Professorship in 2001. She authored four books on Victorian literature and mentored hundreds of graduate students.</p>
        <p>Despite her professional accomplishments, Catherine always said her proudest title was "Mom." She raised three children with grace, humor, and an insistence on proper grammar at the dinner table. Her home was filled with books, classical music, and the aroma of her legendary lemon pound cake.</p>
        <p>She is survived by her children William (Anne) Monroe III, Elizabeth Monroe-Foster, and Dr. James Monroe; eight grandchildren; and two great-grandchildren. She was preceded in death by her husband, William Monroe Jr., in 2019.</p>
        <p>Services will be held at Christ Church, Savannah, on March 5, 2026, at 11:00 AM. Memorial contributions may be made to the Catherine Monroe Scholarship Fund at the University of Georgia.</p>
      </Quote>

      <H3>What makes this work</H3>
      <ul>
        <li>Appropriate formal tone for a professional woman</li>
        <li>Academic and career details balanced with personal warmth</li>
        <li>The detail about "proper grammar at the dinner table" brings her personality alive</li>
        <li>Mentions a memorial scholarship — a lasting tribute</li>
      </ul>

      <H2>Example 3: Faith-Based</H2>
      <Quote>
        <p><strong>Rosa Maria Gutierrez</strong>, 71, entered eternal rest on March 10, 2026, in San Antonio, Texas, after a courageous battle with illness. She is now reunited with her Lord and Savior.</p>
        <p>Rosa was born on December 24, 1954, in Monterrey, Mexico, to Eduardo and Maria Luisa Fernandez. She immigrated to the United States in 1975, where she met and married the love of her life, Antonio Gutierrez, in 1977.</p>
        <p>A devout Catholic, Rosa was an active member of San Fernando Cathedral for over 40 years. She served in the parish choir, the Ladies' Altar Society, and volunteered weekly at the church food pantry. Her faith was not just something she practiced on Sundays — it was the foundation of everything she did.</p>
        <p>Rosa worked as a seamstress for 25 years, and her beautiful handiwork adorned the homes and closets of countless families. She was known for her warm tamales at Christmas, her fierce devotion to her family, and her ability to make anyone feel welcome in her home.</p>
        <p>She is lovingly remembered by her husband Antonio; children Maria (Carlos) Santos, Antonio Jr. (Jennifer) Gutierrez, and Isabel Gutierrez-Morales; twelve grandchildren; and five great-grandchildren.</p>
        <p>A Rosary will be held on March 14 at 7:00 PM, and a Funeral Mass will be celebrated on March 15 at 10:00 AM, both at San Fernando Cathedral. Interment will follow at San Fernando Cemetery No. 2.</p>
      </Quote>

      <H2>Tips for Writing a Mother's Obituary</H2>
      <ul>
        <li><strong>Focus on what defined her:</strong> Was she known for her cooking? Her career? Her volunteering? Lead with what mattered most.</li>
        <li><strong>Include sensory details:</strong> The smell of her kitchen, the sound of her laugh, her favorite saying — these make the obituary come alive.</li>
        <li><strong>Don't forget her maiden name:</strong> Old friends and classmates search by maiden names.</li>
        <li><strong>List survivors carefully:</strong> Check with family about how names and relationships should appear.</li>
        <li><strong>Match the tone to who she was:</strong> A formal woman deserves a formal tone. A fun-loving mom might warrant humor.</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/obituary-examples-for-father': {
    title: 'Obituary Examples for a Father — Templates & Inspiration',
    description: 'Obituary examples for a father including military, professional, and blue-collar templates. Real examples you can customize.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Obituary Examples for a Father</h1>
      <p>A father's obituary should capture the man behind the title — his work ethic, his humor, his quiet sacrifices, and the legacy he leaves behind. Here are three approaches to help you get started.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Example 1: The Working-Class Dad</H2>
      <Quote>
        <p><strong>Robert "Bobby" James Patterson</strong>, 74, of Youngstown, Ohio, passed away on March 8, 2026, at St. Elizabeth Hospital.</p>
        <p>Bobby was born on April 14, 1951, to Richard and June Patterson. He graduated from Chaney High School in 1969 and went to work at Republic Steel the following Monday. He spent 38 years in the mill, retiring in 2007. He never called in sick.</p>
        <p>He married his wife Carol in 1972. Together they raised three kids in the house on Sheridan Road that Bobby built with his own hands — literally. He poured the foundation, framed the walls, and finished the basement over three summers.</p>
        <p>Bobby was a man of few words but many talents. He could fix anything with duct tape and determination. He coached Little League for twelve years, never missed a Friday fish fry at the VFW, and made the best chili in Mahoning County — a fact he'd remind you of regularly.</p>
        <p>He is survived by his wife Carol; children Robert Jr. (Debbie), Michelle (Tony) Morelli, and Brian Patterson; six grandchildren; and his brother, Richard Patterson Jr. He was preceded in death by his parents and his sister, Janet.</p>
        <p>Visitation will be held March 12 from 5-8 PM at Lane Funeral Home. A funeral service will follow on March 13 at 10:00 AM at Holy Name Church.</p>
      </Quote>

      <H2>Example 2: The Military Father</H2>
      <Quote>
        <p><strong>Colonel (Ret.) William Edward Hayes</strong>, 79, of Fayetteville, North Carolina, passed away on March 5, 2026.</p>
        <p>Born on September 22, 1946, in Richmond, Virginia, William graduated from Virginia Military Institute in 1968 and was commissioned as a Second Lieutenant in the United States Army. He served two tours in Vietnam, earning the Bronze Star and Purple Heart.</p>
        <p>Over a distinguished 28-year career, Colonel Hayes served in command and staff positions across the globe, including assignments in Germany, South Korea, and the Pentagon. He retired from Fort Bragg in 1996.</p>
        <p>In retirement, Bill — as his friends called him — devoted himself to his family, his garden, and mentoring young ROTC cadets at Methodist University. He was a member of the VFW, the American Legion, and the VMI Alumni Association.</p>
        <p>He married Nancy Collins in 1970. She was, as he often said, "the bravest person I ever met — she married an infantry officer." Together they raised two children through countless PCS moves with grace and good humor.</p>
        <p>He is survived by his wife Nancy; children Captain (Ret.) William E. Hayes Jr. (Sarah) and Dr. Emily Hayes-Richardson (Mark); five grandchildren; and his brother, Thomas Hayes of Richmond.</p>
        <p>A funeral service with full military honors will be held at the Main Post Chapel, Fort Liberty, on March 10 at 1:00 PM. Interment at Arlington National Cemetery will follow at a later date. In lieu of flowers, donations may be made to the Wounded Warrior Project.</p>
      </Quote>

      <H2>Example 3: The Professional Father</H2>
      <Quote>
        <p><strong>Michael Andrew Chen</strong>, 67, of Portland, Oregon, died unexpectedly on March 12, 2026.</p>
        <p>Michael was born on January 8, 1959, in San Francisco to immigrants Henry and Mei-Lin Chen. The first in his family to attend college, he graduated from UC Berkeley with a degree in engineering and later earned his MBA from Stanford.</p>
        <p>He founded Chen Engineering Solutions in 1992, growing it from a two-person operation in his garage to a firm with 200 employees across three states. He was a mentor to countless young engineers and believed deeply in creating opportunities for first-generation Americans.</p>
        <p>Michael was an avid pilot, a terrible golfer (by his own admission), and the world's most enthusiastic Little League umpire. He read voraciously, traveled extensively, and had a gift for making everyone around him feel like the most important person in the room.</p>
        <p>He is survived by his wife of 38 years, Jennifer; children David (Amy) Chen and Sophia Chen; his mother, Mei-Lin Chen; and two grandchildren.</p>
        <p>A memorial service will be held March 18 at 3:00 PM at First Congregational Church, Portland. In lieu of flowers, the family requests donations to the Chen Family Scholarship Fund at UC Berkeley.</p>
      </Quote>

      <H2>Tips for Writing a Father's Obituary</H2>
      <ul>
        <li><strong>Include his work:</strong> For many men, their career was central to their identity. Honor that.</li>
        <li><strong>Show his personality:</strong> Was he the strong-silent type? The jokester? The coach? Let his character come through.</li>
        <li><strong>Don't overlook hobbies:</strong> Fishing, woodworking, coaching — these details help people remember the whole person.</li>
        <li><strong>Military service matters:</strong> Include rank, branch, decorations, and years of service. Veterans' organizations can help with details.</li>
        <li><strong>Use his voice:</strong> If he had a favorite saying or catchphrase, include it. It brings him to life.</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/obituary-examples-for-spouse': {
    title: 'Obituary Examples for a Spouse — Husband & Wife Templates',
    description: 'Obituary examples for a husband or wife. Learn how to honor a life partnership while balancing personal grief with public tribute.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Obituary Examples for a Spouse</h1>
      <p>Writing an obituary for your husband or wife requires balancing your private grief with a public tribute. Your spouse was known to many people in many ways — partner, parent, colleague, friend. These examples show how to honor the full picture.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Example 1: For a Husband — Warm & Loving</H2>
      <Quote>
        <p><strong>Thomas "Tom" Daniel O'Brien</strong>, 68, of Naperville, Illinois, passed away on March 14, 2026, with his family by his side.</p>
        <p>Tom was born on March 3, 1958, in Chicago. He graduated from Brother Rice High School and earned his degree in accounting from DePaul University. He spent his career at Midwestern Financial, where he rose from junior analyst to CFO over 34 years.</p>
        <p>Tom married the love of his life, Patricia "Patty" Callahan, on October 11, 1986. For nearly 40 years, they were inseparable — dancing at weddings, arguing over directions on road trips, and building a life that was, as Tom would say, "better than I deserved."</p>
        <p>He was an extraordinary father to his three children, attending every game, recital, and parent-teacher conference. He coached soccer, taught his kids to change a tire, and always had dad jokes ready whether you wanted them or not.</p>
        <p>Tom is survived by his wife Patty; children Brendan (Katie), Meghan (Ryan) Kowalski, and Sean O'Brien; four grandchildren; and his siblings, Mary, Kevin, and Patrick O'Brien.</p>
        <p>Visitation will be held March 18, 3-8 PM, at Friedrich-Jones Funeral Home. A funeral Mass will be celebrated March 19 at 10 AM at SS. Peter and Paul Catholic Church.</p>
      </Quote>

      <H2>Example 2: For a Wife — Celebrating Her Spirit</H2>
      <Quote>
        <p><strong>Linda Sue Morrison</strong>, 72, of Asheville, North Carolina, passed away on March 11, 2026.</p>
        <p>Linda was born on August 19, 1953, in Knoxville, Tennessee. She attended the University of Tennessee, where she earned a degree in art education — and where she met a shy engineering student named David Morrison at a Homecoming bonfire in 1973.</p>
        <p>They married in 1975 and spent 51 years proving that opposites attract. Where David was quiet, Linda was the life of the party. Where he was cautious, she was the first one to say yes. Their partnership worked because they balanced each other perfectly.</p>
        <p>Linda taught art in the Buncombe County school system for 28 years. Her students will remember her as the teacher who believed in them when no one else did. Her family will remember her as the woman who turned every Tuesday into Taco Tuesday, every snow day into an adventure, and every goodbye into "I love you."</p>
        <p>She is survived by her husband David; their children Scott (Jennifer) Morrison and Amanda (Brian) Whitfield; five grandchildren; and her sister, Donna Reynolds of Knoxville.</p>
        <p>A celebration of life will be held at the Asheville Art Museum on March 20 at 4:00 PM — because Linda would have wanted to be surrounded by art one last time.</p>
      </Quote>

      <H2>Example 3: After a Long Illness</H2>
      <Quote>
        <p><strong>James Richard Nakamura</strong>, 61, of Seattle, Washington, passed away on March 9, 2026, after a five-year battle with ALS, surrounded by the family he cherished.</p>
        <p>James — Jamie to his friends — was born on July 4, 1964, in Honolulu, Hawaii. He graduated from Punahou School and earned degrees from the University of Washington in both computer science and music.</p>
        <p>Jamie met his wife, Karen, at a jazz club in Capitol Hill in 1990. He asked her to dance. She said no. He asked again. She said yes. That persistence defined everything about Jamie — in love, in work, and in his fight against ALS.</p>
        <p>He spent 25 years at Microsoft, where he led development teams and was known for writing code as elegant as the jazz piano he played every evening. Even after his diagnosis, he continued working, composing music, and making his family laugh.</p>
        <p>He is survived by his wife Karen; children Alexander and Grace Nakamura; his mother, June Nakamura; and his brother, David Nakamura.</p>
        <p>A private family service has been held. A public celebration of Jamie's life will take place April 5 at Jazz Alley, Seattle. In lieu of flowers, please donate to the ALS Association.</p>
      </Quote>

      <H2>Writing Tips for a Spouse's Obituary</H2>
      <ul>
        <li><strong>Share your love story:</strong> How you met is something people want to know. A sentence or two adds warmth.</li>
        <li><strong>It's okay to be emotional:</strong> This isn't a legal document. Tenderness belongs here.</li>
        <li><strong>Ask others for input:</strong> Your children, in-laws, or close friends may remember details you'd miss.</li>
        <li><strong>Consider having someone else write it:</strong> If your grief is too fresh, ask a family member or use a tool like ObitAuthor to help.</li>
        <li><strong>Include inside jokes or sayings:</strong> "Better than I deserved" or "she said no — he asked again" bring a person to life.</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/obituary-examples-for-grandparent': {
    title: 'Obituary Examples for a Grandparent — Templates & Inspiration',
    description: 'Obituary examples for a grandmother or grandfather. Capture their generational legacy with these customizable templates.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Obituary Examples for a Grandparent</h1>
      <p>Grandparents hold a unique place in our hearts. They represent continuity, tradition, and unconditional love. When writing their obituary, you have the opportunity to capture not just a life but a legacy that spans generations.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Example 1: For a Grandmother</H2>
      <Quote>
        <p><strong>Evelyn Grace Carter</strong>, 91, of Lexington, Kentucky, passed away peacefully on March 7, 2026, at her home — the same home where she raised four children, hosted 50 Thanksgivings, and made the best biscuits in Fayette County.</p>
        <p>Evelyn was born on February 14, 1935, Valentine's Day, which she considered proof that she was "made for love." She married Harold Carter in 1955, and together they farmed 200 acres of bluegrass country for over four decades.</p>
        <p>She was a member of First Baptist Church of Lexington for 70 years, where she taught Sunday school, sang in the choir, and organized the annual church picnic. She was a 4-H leader, a poll worker for every election since Eisenhower, and could tell you the name of every child born on her road for the past sixty years.</p>
        <p>Known as "Mamaw" to her 14 grandchildren and 22 great-grandchildren, Evelyn had a standing rule: any grandchild who visited got homemade biscuits and a $5 bill tucked into their pocket before they left.</p>
        <p>She was preceded in death by her husband Harold in 2018 and her son, Harold Jr., in 2010. She is survived by her children Martha (Gene) Dawson, Robert (Linda) Carter, and Beverly Carter-Simms; 14 grandchildren; 22 great-grandchildren; and three great-great-grandchildren.</p>
        <p>Funeral services will be held March 12 at 11:00 AM at First Baptist Church, Lexington. The family kindly asks that in lieu of flowers, you make a batch of biscuits and share them with someone you love.</p>
      </Quote>

      <H2>Example 2: For a Grandfather</H2>
      <Quote>
        <p><strong>Frank Joseph DiNapoli Sr.</strong>, 88, of Providence, Rhode Island, died on March 10, 2026, at Rhode Island Hospital.</p>
        <p>Frank was born on April 3, 1937, in Federal Hill, Providence, to Giuseppe and Carmela DiNapoli, Italian immigrants who ran a corner bakery. Frank grew up dusted in flour and dreaming big.</p>
        <p>After graduating from La Salle Academy, Frank served in the United States Army during the Korean War era. Upon returning home, he opened DiNapoli's Restaurant in 1962, which became a Providence institution for nearly five decades. Governors, mayors, and generations of families broke bread at Frank's tables.</p>
        <p>Frank married Rosemary Capaldi in 1960. Their partnership — in life and in the restaurant — was legendary. They worked side by side for 47 years until Rosemary's passing in 2007.</p>
        <p>"Papa Frank" adored his grandchildren. He taught each one how to make Sunday gravy, snuck them cannoli before dinner, and told them the same stories about "the old neighborhood" until they could recite them by heart.</p>
        <p>He is survived by his children Frank Jr. (Lisa), Maria (Anthony) Russo, and Joseph (Diane) DiNapoli; 11 grandchildren; and 8 great-grandchildren.</p>
        <p>Calling hours will be March 14, 4-8 PM, at Calvary Cemetery Chapel. A Mass of Christian Burial will be celebrated March 15 at 10:00 AM at Holy Ghost Church.</p>
      </Quote>

      <H2>Example 3: For a Long-Lived Grandparent</H2>
      <Quote>
        <p><strong>Dorothy Mae Johnson</strong>, 99, of Minneapolis, Minnesota, passed away on March 13, 2026 — just three months shy of her 100th birthday.</p>
        <p>Born on June 15, 1926, during the Roaring Twenties, Dorothy lived through the Great Depression, World War II, the Moon Landing, the fall of the Berlin Wall, and the invention of the iPhone — which she refused to use, insisting that "if Alexander Graham Bell's phone was good enough, it's good enough for me."</p>
        <p>Dorothy married Arthur Johnson in 1948. They raised five children in the house on Cedar Avenue that Arthur built. After his death in 1995, Dorothy lived independently for another 28 years, mowing her own lawn until age 94.</p>
        <p>She worked as a telephone operator for Northwestern Bell for 30 years, volunteered at Abbott Northwestern Hospital, and was a founding member of her neighborhood book club, which has met continuously since 1971.</p>
        <p>Dorothy is survived by her children Arthur Jr. (Pat), Karen (Mike) Lindgren, James (Sandra), and Susan (Dave) Erickson; 16 grandchildren; 31 great-grandchildren; and 4 great-great-grandchildren. She was preceded in death by her husband Arthur and her daughter, Mary Ellen Johnson.</p>
        <p>A funeral service will be held March 18 at 1:00 PM at Mount Olivet Lutheran Church. In lieu of flowers, the family asks that you call someone you love — on a landline, if Dorothy had her way.</p>
      </Quote>

      <H2>Tips for Writing a Grandparent's Obituary</H2>
      <ul>
        <li><strong>Capture their era:</strong> Grandparents lived through history. A line about the times they witnessed adds depth.</li>
        <li><strong>Use their grandparent name:</strong> Mamaw, Papa, Nana, Opa — whatever the grandchildren called them is personal and meaningful.</li>
        <li><strong>Count the descendants:</strong> The sheer number of people they leave behind is part of their legacy.</li>
        <li><strong>Include traditions:</strong> Sunday dinners, holiday recipes, bedtime stories — these are what grandchildren remember most.</li>
        <li><strong>End with personality:</strong> A humorous or touching final request (biscuits, landlines) leaves a lasting impression.</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/obituary-templates-by-tone': {
    title: 'Obituary Templates by Tone — Simple, Heartfelt, Religious, Celebratory & Formal',
    description: 'Fill-in-the-blank obituary templates organized by tone. Choose simple, heartfelt, religious, celebratory, or formal styles.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Obituary Templates by Tone</h1>
      <p>Not sure what tone to strike? The right tone depends on who the person was, what the family prefers, and where the obituary will be published. Below are fill-in-the-blank templates for five common styles.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Simple & Straightforward</H2>
      <p>Best for: newspaper submissions with word limits, or families who prefer brevity.</p>
      <Quote>
        <p><strong>[Full Name]</strong>, [age], of [City, State], passed away on [date].</p>
        <p>[He/She] was born on [date] in [birthplace] to [parents' names]. [He/She] worked as a [occupation] for [number] years and enjoyed [hobby/interest].</p>
        <p>[He/She] is survived by [spouse/children/grandchildren names]. [He/She] was preceded in death by [names].</p>
        <p>Services will be held at [location] on [date] at [time]. [Optional: In lieu of flowers, donations may be made to (organization).]</p>
      </Quote>

      <H2>Heartfelt & Personal</H2>
      <p>Best for: online memorials, funeral programs, or when the family wants something more emotional.</p>
      <Quote>
        <p><strong>[Full Name]</strong>, [age], of [City, State], left this world on [date], leaving behind a community of people whose lives [he/she] made immeasurably better.</p>
        <p>[Name] was born on [date] in [birthplace]. From [his/her] earliest days, [he/she] was known for [defining trait — kindness, humor, determination]. [He/She] had a way of [specific behavior — making strangers feel like friends / lighting up a room / turning ordinary moments into memories].</p>
        <p>[He/She] married [spouse name] in [year], and together they built a life defined by [love, adventure, faith, laughter — choose what fits]. [He/She] was the kind of [mother/father/partner] who [specific example].</p>
        <p>[Paragraph about career, hobbies, or community involvement with personal anecdotes.]</p>
        <p>[He/She] is survived by [list]. [He/She] was preceded in death by [list].</p>
        <p>[Service details.] [He/She] will be profoundly missed, but [his/her] legacy of [love/kindness/generosity] lives on in everyone [he/she] touched.</p>
      </Quote>

      <H2>Religious & Faith-Based</H2>
      <p>Best for: families of faith who want scripture and spiritual language.</p>
      <Quote>
        <p><strong>[Full Name]</strong>, [age], of [City, State], was called home to [the Lord / eternal rest / be with God] on [date].</p>
        <p>[Name] was born on [date] in [birthplace] to [parents]. [He/She] was baptized at [church] and remained a faithful member of [church/parish/synagogue/mosque] throughout [his/her] life.</p>
        <p>[He/She] lived [his/her] faith daily through [acts of service, prayer, charity]. [Specific example: led Bible study, organized church dinners, sang in the choir for 40 years].</p>
        <p>[Family and career paragraph].</p>
        <p>[He/She] is survived by [list]. [He/She] was preceded in death by [list], with whom [he/she] is now joyfully reunited.</p>
        <p>[Service details including rosary, Mass, or other religious ceremonies.] [Scripture verse — e.g., "Well done, good and faithful servant." — Matthew 25:23]</p>
      </Quote>

      <H2>Celebratory & Uplifting</H2>
      <p>Best for: someone who lived life to the fullest and would want people to smile, not cry.</p>
      <Quote>
        <p><strong>[Full Name]</strong> — [nickname if applicable] — left this world on [date], and wherever [he/she] is now, we're sure [he/she]'s already [humorous or characteristic action — rearranging the furniture / telling everyone how to do it better / looking for the nearest golf course].</p>
        <p>[Name] packed [age] years with more living than most people manage in two lifetimes. Born on [date] in [birthplace], [he/she] [brief origin story with flair].</p>
        <p>[Career and life highlights told with energy and personality. Include funny anecdotes, famous sayings, or legendary stories.]</p>
        <p>[He/She] is survived by [list] and an extended family of friends who loved [him/her] fiercely.</p>
        <p>[Service details.] In [his/her] honor, the family asks that you [do something the person would have loved — tell a good joke, have a cold beer, hug someone, etc.].</p>
      </Quote>

      <H2>Formal & Traditional</H2>
      <p>Best for: prominent community members, professionals, or when the obituary will appear in a major newspaper.</p>
      <Quote>
        <p><strong>[Full Name with titles — Dr., Hon., Col., etc.]</strong>, [age], of [City, State], died on [date] at [location].</p>
        <p>Born on [date] in [birthplace] to [parents with titles if applicable], [Name] received [his/her] education at [schools with degrees and honors]. [He/She] went on to [career summary with positions, organizations, and notable achievements].</p>
        <p>[He/She] was a member of [professional organizations, boards, clubs]. [His/Her] contributions to [field/community] were recognized with [awards, honors, named positions].</p>
        <p>[He/She] married [spouse full name] in [year]. Together they are the parents of [children with full names and spouses].</p>
        <p>[He/She] is survived by [complete list]. [He/She] was preceded in death by [list].</p>
        <p>Services will be held at [location] on [date] at [time]. Memorial contributions may be directed to [organization].</p>
      </Quote>

      <H2>Choosing the Right Tone</H2>
      <ul>
        <li><strong>Consider the person:</strong> Would they want formality or humor? Match the tone to their personality.</li>
        <li><strong>Consider the audience:</strong> A newspaper obituary may call for more formality than an online memorial.</li>
        <li><strong>Consider the family:</strong> Everyone grieves differently. Get consensus on tone before publishing.</li>
        <li><strong>You can mix styles:</strong> A formal opening with a heartfelt middle and a celebratory ending often works beautifully.</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/obituary-sections-guide': {
    title: 'Obituary Sections Guide — What to Include & How to Write Each Part',
    description: 'A complete guide to every section of an obituary: opening line, biography, family list, service details, and more. With examples.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Obituary Sections Guide</h1>
      <p>Every obituary is made up of the same basic building blocks. Understanding each section — what to include, what to skip, and how to phrase it — makes the writing process much less daunting.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>1. The Opening Line</H2>
      <p>This establishes who passed, when, and where. It's the most structured part of the obituary.</p>
      <H3>Standard format:</H3>
      <Quote><p>[Full Name], [age], of [City, State], passed away on [Date] at [location — hospital, home, etc.].</p></Quote>
      <H3>Variations:</H3>
      <ul>
        <li><em>"...died peacefully at home, surrounded by family."</em></li>
        <li><em>"...entered eternal rest on..."</em> (faith-based)</li>
        <li><em>"...left this world on his own terms on..."</em> (celebratory)</li>
      </ul>
      <p><strong>Tip:</strong> Include the city and state — people search obituaries by location.</p>

      <H2>2. Biographical Information</H2>
      <p>Birth date, birthplace, parents, and education. This grounds the person in their history.</p>
      <Quote><p>[Name] was born on [date] in [city, state] to [parents' names]. [He/She] graduated from [high school] in [year] and earned [degree] from [university] in [year].</p></Quote>
      <p><strong>Do:</strong> Include maiden names for women (helps old friends find the obituary). Include parents' names even if deceased.</p>
      <p><strong>Don't:</strong> List every school if they transferred multiple times. Stick to the highlights.</p>

      <H2>3. Career & Accomplishments</H2>
      <p>What they did professionally and in the community.</p>
      <Quote><p>[Name] worked as a [job title] at [company/organization] for [years], where [he/she] [notable accomplishment]. [He/She] also served as [volunteer role, board member, etc.].</p></Quote>
      <p><strong>Tip:</strong> Focus on 1-3 career highlights, not a full resume. What would they want to be remembered for?</p>

      <H2>4. Personal Life & Interests</H2>
      <p>This is where personality shines through. Hobbies, passions, habits, and funny quirks.</p>
      <Quote><p>[Name] was an avid [hobby] enthusiast. [He/She] loved [activity], was known for [characteristic behavior], and could often be found [specific scene — in the garden, at the lake, cheering at games].</p></Quote>
      <p><strong>Tip:</strong> Include sensory details. "She tended her rose garden every morning before dawn" is better than "She liked gardening."</p>

      <H2>5. Family & Survivors</H2>
      <p>List surviving family members and those who preceded in death.</p>
      <H3>Standard format:</H3>
      <Quote>
        <p>[Name] is survived by [his/her] [wife/husband], [name]; children [names with spouses in parentheses]; [number] grandchildren; and [siblings].</p>
        <p>[He/She] was preceded in death by [parents, siblings, or others].</p>
      </Quote>
      <H3>Guidelines:</H3>
      <ul>
        <li>List spouse first, then children (usually oldest to youngest), then grandchildren, then siblings</li>
        <li>Include spouses in parentheses: "Michael (Sarah) Thompson"</li>
        <li>For large families, you can summarize: "14 grandchildren and 22 great-grandchildren"</li>
        <li>Check with ALL family members about how they'd like to be listed</li>
      </ul>

      <H2>6. Service Details</H2>
      <p>Where and when people can pay their respects.</p>
      <Quote>
        <p>Visitation will be held on [day, date] from [time] to [time] at [funeral home name and address].</p>
        <p>A [funeral service / Mass / celebration of life] will be held on [day, date] at [time] at [location]. Interment will follow at [cemetery].</p>
      </Quote>
      <p><strong>Tip:</strong> Include full addresses for out-of-town visitors. If the service is private, say so: "A private family service will be held."</p>

      <H2>7. Memorial Donations</H2>
      <p>If the family prefers donations over flowers.</p>
      <Quote><p>In lieu of flowers, the family requests memorial donations to [organization name], [address or website].</p></Quote>
      <p><strong>Tip:</strong> Choose organizations that meant something to the deceased. Include a website or address to make donating easy.</p>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/how-to-write-an-obituary-in-30-minutes': {
    title: 'How to Write an Obituary in 30 Minutes — A Step-by-Step Guide',
    description: 'A timed, step-by-step guide to writing a complete obituary in 30 minutes. Perfect when time is short and emotions are high.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">How to Write an Obituary in 30 Minutes</h1>
      <p>The funeral home needs the obituary. The newspaper has a deadline. Your family is waiting. You're grieving. Here's a practical, no-nonsense guide to writing a complete obituary in 30 minutes or less.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Before You Start (2 Minutes)</H2>
      <p>Gather these items if available:</p>
      <ul>
        <li>A photo of the deceased (for reference, not necessarily for the obituary)</li>
        <li>Dates: birth, death, marriage</li>
        <li>List of immediate family members</li>
        <li>Service/visitation details (or call the funeral home)</li>
      </ul>
      <p>Don't have everything? That's okay. You can fill in gaps later. Start writing now.</p>

      <H2>Minutes 1-5: The Facts</H2>
      <p>Write the opening paragraph. This is the easiest part because it's pure information:</p>
      <Quote>
        <p>[Full Name], [age], of [City, State], passed away on [Date of Death].</p>
        <p>[He/She] was born on [Date of Birth] in [Birthplace] to [Parents' Names].</p>
      </Quote>
      <p>That's it. Two sentences. You're already started.</p>

      <H2>Minutes 5-10: The Life Story</H2>
      <p>Write 2-3 sentences about their education, career, and one personal detail. Don't overthink it:</p>
      <Quote>
        <p>[Name] graduated from [school] and worked as a [job] for [years] at [company]. [He/She] was known for [one defining trait — generosity, humor, dedication]. [He/She] loved [one hobby or interest].</p>
      </Quote>
      <p><strong>Shortcut:</strong> Ask yourself: "If I had to describe them to a stranger in 30 seconds, what would I say?" Write that.</p>

      <H2>Minutes 10-15: The Family</H2>
      <p>List survivors and those who preceded in death:</p>
      <Quote>
        <p>[Name] is survived by [spouse]; children [names]; [number] grandchildren; and [siblings]. [He/She] was preceded in death by [names].</p>
      </Quote>
      <p><strong>Shortcut:</strong> If you can't reach everyone for exact names/spellings, write what you know and add "[family details to be updated]." You can amend it later.</p>

      <H2>Minutes 15-20: Service Details</H2>
      <Quote>
        <p>Visitation: [day, date, time, location].</p>
        <p>Service: [day, date, time, location].</p>
        <p>In lieu of flowers: [organization] or "the family gratefully accepts flowers and condolences."</p>
      </Quote>

      <H2>Minutes 20-25: Add One Personal Touch</H2>
      <p>Go back and add one sentence that makes this obituary theirs. Something only their family would know:</p>
      <ul>
        <li>A favorite saying</li>
        <li>A legendary recipe or hobby</li>
        <li>The way they laughed, their morning routine, their terrible puns</li>
      </ul>
      <p>This single detail elevates the obituary from generic to personal.</p>

      <H2>Minutes 25-30: Review & Submit</H2>
      <ul>
        <li>Read it aloud once</li>
        <li>Check spelling of all names</li>
        <li>Verify dates and times</li>
        <li>Confirm service locations with the funeral home</li>
        <li>Send it to one other family member for a quick review</li>
      </ul>

      <H2>Still Feeling Stuck?</H2>
      <p>If 30 minutes feels like too much pressure, our AI tool can generate a beautiful, personalized obituary in under 5 minutes. Just answer a few questions and we'll handle the writing.</p>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/what-not-to-include-in-an-obituary': {
    title: 'What Not to Include in an Obituary — Common Mistakes to Avoid',
    description: 'Learn what to leave out of an obituary: cause of death debates, family feuds, oversharing, and other common mistakes.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">What Not to Include in an Obituary</h1>
      <p>Knowing what to write in an obituary is important — but knowing what to leave out can be just as crucial. An obituary is a public document, often the first search result for someone's name. Here are the most common mistakes and how to avoid them.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>1. Cause of Death (Usually)</H2>
      <p>Unless the family specifically wants to include it, the cause of death is typically omitted. This is especially true for:</p>
      <ul>
        <li>Suicide — mental health organizations recommend careful language; consult guidelines from the American Foundation for Suicide Prevention</li>
        <li>Overdose — unless the family wants to raise awareness</li>
        <li>Ongoing legal matters</li>
      </ul>
      <p><strong>Acceptable alternatives:</strong> "after a courageous battle with illness," "after a brief illness," "passed unexpectedly," or simply no mention at all.</p>
      <p><strong>Exception:</strong> Some families choose to include it to raise awareness or reduce stigma. This is a personal decision and should be respected.</p>

      <H2>2. Family Conflicts or Estrangements</H2>
      <p>An obituary is not the place to settle scores or air grievances. Avoid:</p>
      <ul>
        <li>Pointedly excluding family members</li>
        <li>Passive-aggressive phrasing ("survived by his children, some of whom cared enough to visit")</li>
        <li>Revealing private family disputes</li>
      </ul>
      <p><strong>Better approach:</strong> If listing someone feels wrong, simply don't include detailed family lists. A general "survived by family and friends" is acceptable.</p>

      <H2>3. Financial Information</H2>
      <p>Never include:</p>
      <ul>
        <li>The deceased's net worth or assets</li>
        <li>Inheritance details</li>
        <li>Debts or financial struggles</li>
        <li>Specific property holdings</li>
      </ul>
      <p>Obituaries are indexed by search engines. Financial information attracts scammers targeting bereaved families.</p>

      <H2>4. Exact Home Address</H2>
      <p>Obituaries announce that a home may be empty during services. Never include the full street address. Use "of [City, State]" instead.</p>

      <H2>5. Dates That Enable Identity Theft</H2>
      <p>Be cautious with information that could be used for fraud:</p>
      <ul>
        <li>Full birth date (month and year are sufficient if you're concerned)</li>
        <li>Mother's maiden name (though this is traditionally included)</li>
        <li>Social Security numbers (obviously — but it has happened)</li>
      </ul>

      <H2>6. Overly Negative Content</H2>
      <p>An obituary should honor a life, even an imperfect one. Avoid:</p>
      <ul>
        <li>Criticisms of the deceased's choices</li>
        <li>Blaming doctors, hospitals, or others</li>
        <li>Airing dirty laundry about relationships, addiction, or behavior</li>
      </ul>
      <p><strong>The rule of thumb:</strong> Would you say this at the funeral, in front of everyone? If not, leave it out.</p>

      <H2>7. Clichés Without Substance</H2>
      <p>These phrases aren't "wrong" but they don't add anything:</p>
      <ul>
        <li>"She never met a stranger" (unless you show how)</li>
        <li>"He lit up every room" (show a specific example instead)</li>
        <li>"She had a heart of gold" (describe how she showed it)</li>
      </ul>
      <p><strong>Better:</strong> Replace clichés with specific details. "She once drove 200 miles in a snowstorm to bring soup to a sick neighbor" says more than "She had a heart of gold."</p>

      <H2>8. Incomplete or Unverified Information</H2>
      <ul>
        <li>Double-check spelling of all names</li>
        <li>Verify dates with official records if possible</li>
        <li>Confirm service times and locations with the funeral home</li>
        <li>Don't guess at military ranks, degrees, or awards</li>
      </ul>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/free-obituary-template': {
    title: 'Free Obituary Template — Complete Fill-in-the-Blank Guide',
    description: 'A free, complete obituary template with fill-in-the-blank sections. Ready for newspaper or online use. Print-friendly.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Free Obituary Template</h1>
      <p>Copy this template, fill in the blanks, and you'll have a complete, well-structured obituary. This format works for newspapers, funeral home websites, and online memorials.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Complete Obituary Template</H2>
      <Quote>
        <p><strong>[FULL LEGAL NAME]</strong>, [AGE], of [CITY, STATE], passed away [peacefully / unexpectedly / after a courageous battle with illness] on [DAY OF WEEK], [FULL DATE], at [LOCATION — home, hospital name, etc.].</p>
        <p>[FIRST NAME] was born on [BIRTH DATE] in [BIRTH CITY, STATE] to [FATHER'S FULL NAME] and [MOTHER'S FULL NAME (MAIDEN NAME)]. [He/She] graduated from [HIGH SCHOOL NAME] in [YEAR] and [earned a degree in / attended / studied] [FIELD] at [COLLEGE/UNIVERSITY] in [YEAR].</p>
        <p>On [WEDDING DATE], [he/she] married [SPOUSE'S FULL NAME] in [CITY, STATE]. Together they shared [NUMBER] years of [love / marriage / partnership / adventure].</p>
        <p>[FIRST NAME] worked as a [JOB TITLE] at [COMPANY/ORGANIZATION] for [NUMBER] years, where [he/she] [ONE NOTABLE ACCOMPLISHMENT OR QUALITY]. [He/She] was also [active in / a member of / a volunteer at] [ORGANIZATIONS, CHURCHES, CLUBS].</p>
        <p>[FIRST NAME] was known for [DEFINING PERSONAL QUALITY]. [He/She] loved [HOBBY 1], [HOBBY 2], and [HOBBY 3]. [INCLUDE ONE SPECIFIC PERSONAL DETAIL OR ANECDOTE THAT CAPTURES WHO THEY WERE.]</p>
        <p>[He/She] is survived by [his/her] [wife/husband], [NAME]; [his/her] children, [NAME (SPOUSE)] of [CITY], [NAME (SPOUSE)] of [CITY], and [NAME] of [CITY]; [NUMBER] grandchildren; [NUMBER] great-grandchildren; and [his/her] [siblings/other relatives], [NAMES].</p>
        <p>[He/She] was preceded in death by [RELATIONSHIP AND NAMES].</p>
        <p>Visitation will be held on [DAY, DATE] from [TIME] to [TIME] at [FUNERAL HOME NAME, ADDRESS]. A [funeral service / celebration of life / Mass of Christian Burial] will be held on [DAY, DATE] at [TIME] at [LOCATION, ADDRESS]. Interment will follow at [CEMETERY NAME].</p>
        <p>In lieu of flowers, memorial donations may be made to [ORGANIZATION NAME], [ADDRESS OR WEBSITE].</p>
      </Quote>

      <H2>Formatting Guidelines</H2>
      <H3>For Newspapers</H3>
      <ul>
        <li>Most newspapers charge by the line or word — aim for 200-300 words for a standard listing</li>
        <li>Use standard formatting (no bold, italics, or special characters)</li>
        <li>Include the deceased's full name exactly as it should appear</li>
        <li>Ask about photo options and costs</li>
      </ul>

      <H3>For Online Memorials</H3>
      <ul>
        <li>No word limit — include as much detail as you'd like</li>
        <li>Add photos, videos, and links to charitable organizations</li>
        <li>Consider a guestbook feature for condolences</li>
        <li>Include the deceased's maiden name and any nicknames for searchability</li>
      </ul>

      <H2>Word Count Recommendations</H2>
      <ul>
        <li><strong>Brief notice:</strong> 50-100 words (facts only — name, dates, survivors, services)</li>
        <li><strong>Standard obituary:</strong> 200-400 words (adds biography and personal details)</li>
        <li><strong>Extended tribute:</strong> 500-1,000+ words (full life story with anecdotes)</li>
      </ul>

      <H2>Want Something More Personalized?</H2>
      <p>This template gives you the structure, but our AI obituary generator creates truly personalized obituaries with the warmth and nuance that a template can't provide. Answer a few questions, and we'll craft something beautiful.</p>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },

  '/obituary-generator-by-religion': {
    title: 'Obituary Guide for Different Religions & Cultures',
    description: 'How to write an obituary for Christian, Catholic, Jewish, Islamic, Hindu, Buddhist, and secular traditions. Key phrases and customs.',
    render: (onGetStarted) => (<>
      <h1 className="commercial-heading mb-3">Obituary Guide for Different Religions & Cultures</h1>
      <p>Different faiths and cultures have distinct traditions around death, mourning, and how a life is memorialized. Using the right language and observing the right customs shows respect for the deceased and their community.</p>

      <CTA onGetStarted={onGetStarted} />

      <H2>Christian (Protestant)</H2>
      <H3>Key phrases:</H3>
      <ul>
        <li>"Went home to be with the Lord"</li>
        <li>"Entered into eternal rest"</li>
        <li>"Is now in the arms of Jesus"</li>
      </ul>
      <H3>Customs to mention:</H3>
      <ul>
        <li>Church membership and involvement</li>
        <li>Baptism, confirmation, or other milestones</li>
        <li>Scripture verse (John 14:2-3, Psalm 23, Philippians 1:21 are common)</li>
      </ul>
      <Quote><p><em>"[Name] entered into eternal rest on [date], and is now in the loving arms of her Savior. A funeral service will be held at [Church Name]..."</em></p></Quote>

      <H2>Catholic</H2>
      <H3>Key phrases:</H3>
      <ul>
        <li>"Was called home to God"</li>
        <li>"Entered eternal life"</li>
        <li>"Fortified by the Sacraments of Holy Mother Church"</li>
      </ul>
      <H3>Customs to mention:</H3>
      <ul>
        <li>Parish membership</li>
        <li>Rosary vigil (typically the evening before the funeral)</li>
        <li>Mass of Christian Burial (not "funeral service")</li>
        <li>Interment details</li>
        <li>Charitable donations may be directed to Catholic organizations</li>
      </ul>
      <Quote><p><em>"A Rosary will be recited on [date] at [time] at [location]. A Mass of Christian Burial will be celebrated on [date] at [time] at [Church Name]."</em></p></Quote>

      <H2>Jewish</H2>
      <H3>Key phrases:</H3>
      <ul>
        <li>"May their memory be a blessing" (zichrono/zichronah livracha)</li>
        <li>"Passed away" (avoid "passed on" or "lost")</li>
      </ul>
      <H3>Customs to mention:</H3>
      <ul>
        <li>Burial typically occurs within 24-48 hours</li>
        <li>Shiva (mourning period) details — dates, location, times</li>
        <li>Temple/synagogue affiliation</li>
        <li>"In lieu of flowers" is especially common (flowers are not traditional)</li>
        <li>Donations to Jewish charities or the synagogue</li>
      </ul>
      <Quote><p><em>"Graveside services will be held on [date] at [cemetery]. The family will observe Shiva at [address] from [dates]. In lieu of flowers, contributions may be made to [organization]. May his memory be a blessing."</em></p></Quote>

      <H2>Islamic</H2>
      <H3>Key phrases:</H3>
      <ul>
        <li>"Inna lillahi wa inna ilayhi raji'un" (To God we belong and to Him we shall return)</li>
        <li>"Returned to Allah"</li>
      </ul>
      <H3>Customs to mention:</H3>
      <ul>
        <li>Burial within 24 hours when possible</li>
        <li>Janazah (funeral prayer) details</li>
        <li>Mosque affiliation</li>
        <li>Flowers are typically not sent; charitable donations preferred</li>
      </ul>
      <Quote><p><em>"Inna lillahi wa inna ilayhi raji'un. [Name] returned to Allah on [date]. Janazah prayers will be held at [Mosque Name] on [date] at [time], followed by burial at [cemetery]."</em></p></Quote>

      <H2>Hindu</H2>
      <H3>Key phrases:</H3>
      <ul>
        <li>"Attained moksha" (liberation)</li>
        <li>"Left for heavenly abode"</li>
        <li>"Om Shanti" (peace)</li>
      </ul>
      <H3>Customs to mention:</H3>
      <ul>
        <li>Cremation is traditional and typically occurs quickly</li>
        <li>13-day mourning period</li>
        <li>Prayer meeting details</li>
        <li>Flowers (especially white) are appropriate</li>
      </ul>

      <H2>Buddhist</H2>
      <H3>Key phrases:</H3>
      <ul>
        <li>"Passed into Nirvana"</li>
        <li>"Transitioned from this life"</li>
      </ul>
      <H3>Customs to mention:</H3>
      <ul>
        <li>Cremation or burial (varies by tradition)</li>
        <li>Chanting services at the temple</li>
        <li>Memorial service timing varies (some traditions hold services on the 7th and 49th days)</li>
        <li>White is the traditional mourning color</li>
      </ul>

      <H2>Non-Religious / Secular</H2>
      <H3>Key phrases:</H3>
      <ul>
        <li>"Passed away" or "died" (direct and honest)</li>
        <li>"Left this world"</li>
        <li>"Will be deeply missed"</li>
      </ul>
      <H3>Approach:</H3>
      <ul>
        <li>Focus on the person's life, impact, and relationships rather than afterlife</li>
        <li>"Celebration of life" rather than "funeral service"</li>
        <li>Include literary quotes, poetry, or personal philosophy instead of scripture</li>
        <li>Memorial donations to secular causes</li>
      </ul>
      <Quote><p><em>"[Name] lived a life defined by curiosity, kindness, and an unwavering commitment to leaving the world better than she found it. A celebration of life will be held..."</em></p></Quote>

      <CTA onGetStarted={onGetStarted} />
    </>),
  },
};

// ─── SEO PAGE PATHS (exported for routing) ──────────────────────

export const SEO_PATHS = Object.keys(PAGES);

// ─── COMPONENT ──────────────────────────────────────────────────

export default function SeoPage({ onGetStarted }) {
  const path = window.location.pathname;
  const page = PAGES[path];

  useEffect(() => {
    if (page) {
      document.title = page.title + ' | ObitAuthor';
      let meta = document.querySelector('meta[name="description"]');
      if (!meta) { meta = document.createElement('meta'); meta.name = 'description'; document.head.appendChild(meta); }
      meta.content = page.description;

      const ld = document.createElement('script');
      ld.type = 'application/ld+json';
      ld.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'Article',
        headline: page.title,
        description: page.description,
        publisher: { '@type': 'Organization', name: 'ObitAuthor', url: 'https://obitauthor.com' },
        datePublished: '2026-03-31',
        dateModified: '2026-03-31',
        mainEntityOfPage: `https://obitauthor.com${path}`,
      });
      document.head.appendChild(ld);
      window.scrollTo(0, 0);
      return () => { document.head.removeChild(ld); };
    }
  }, [path, page]);

  if (!page) {
    return (
      <div className="container py-5 text-center">
        <div className="glass-card">
          <h1 className="commercial-heading">Page Not Found</h1>
          <p>The page you're looking for doesn't exist.</p>
          <a href="/" style={{ color: '#90cdf4' }}>Return home</a>
        </div>
      </div>
    );
  }

  return (
    <div className="container py-5">
      <div className="row justify-content-center">
        <div className="col-lg-10 col-xl-8">
          <div className="glass-card" style={{ lineHeight: 1.7 }}>
            {page.render(onGetStarted)}
            <RelatedLinks current={path} />
          </div>
        </div>
      </div>
    </div>
  );
}
